<template>
    <EntityDetailsPropertiesTabCard
        class="EntityDetailsPropertiesTabPropertiesCard"
        title="Properties"
        :editing="isEditing"
    >
        <transition name="EntityDetailsPropertiesTab__modeFade" mode="out-in">
            <!-- READ MODE -->
            <div v-if="!isEditing" key="read" class="EntityDetailsPropertiesTab__readView">
                <!-- Identity section -->
                <div class="EntityDetailsPropertiesTab__readSection">
                    <span class="EntityDetailsPropertiesTab__readSectionLabel">Identity</span>
                    <div class="EntityDetailsPropertiesTab__readGrid">
                        <span class="EntityDetailsPropertiesTab__readLabel">Legal Name</span>
                        <span
                            class="EntityDetailsPropertiesTab__readValue"
                            :class="{ 'EntityDetailsPropertiesTab__readEmpty': !entity.legalName }"
                        >{{ entity.legalName || '—' }}</span>

                        <span class="EntityDetailsPropertiesTab__readLabel">DBA Name(s)</span>
                        <div
                            v-if="filteredDba.length"
                            class="EntityDetailsPropertiesTab__readMultiValue"
                        >
                            <span
                                v-for="(dba, index) in filteredDba"
                                :key="index"
                                class="EntityDetailsPropertiesTab__readValue"
                            >{{ dba }}</span>
                        </div>
                        <span v-else class="EntityDetailsPropertiesTab__readValue EntityDetailsPropertiesTab__readEmpty">—</span>

                        <template v-if="isEvidentUser">
                            <span class="EntityDetailsPropertiesTab__readLabel">Entity UUID</span>
                            <span
                                :class="[
                                    'EntityDetailsPropertiesTab__readValue',
                                    'EntityDetailsPropertiesTabPropertiesCard__uuid',
                                ]"
                            >
                                {{ entity.id }}
                                <EidIcon
                                    data-test-id="EntityDetailsPropertiesTabPropertiesCard__copyButton"
                                    class="EntityDetailsPropertiesTabPropertiesCard__copyIcon"
                                    :icon-src="copyIcon"
                                    alt="copyIcon"
                                    @click="copyEntityUuid"
                                />
                            </span>
                        </template>
                    </div>
                </div>

                <!-- Contact section -->
                <div class="EntityDetailsPropertiesTab__readSection">
                    <span class="EntityDetailsPropertiesTab__readSectionLabel">Contact</span>
                    <div class="EntityDetailsPropertiesTab__readGrid">
                        <span class="EntityDetailsPropertiesTab__readLabel">Primary Contact Name</span>
                        <span
                            class="EntityDetailsPropertiesTab__readValue"
                            :class="{ 'EntityDetailsPropertiesTab__readEmpty': !entity.contactName }"
                        >{{ entity.contactName || '—' }}</span>

                        <span class="EntityDetailsPropertiesTab__readLabel">Primary Contact Email</span>
                        <span
                            class="EntityDetailsPropertiesTab__readValue"
                            :class="{ 'EntityDetailsPropertiesTab__readEmpty': !entity.contactEmail }"
                        >{{ entity.contactEmail || '—' }}</span>

                        <span class="EntityDetailsPropertiesTab__readLabel">Primary Contact Phone</span>
                        <span
                            class="EntityDetailsPropertiesTab__readValue"
                            :class="{ 'EntityDetailsPropertiesTab__readEmpty': !entity.contactPhoneNumber }"
                        >{{ entity.contactPhoneNumber || '—' }}</span>
                    </div>
                </div>

                <!-- Address section — only shown when an address has been set -->
                <div v-if="hasAddress" class="EntityDetailsPropertiesTab__readSection">
                    <span class="EntityDetailsPropertiesTab__readSectionLabel">Address</span>
                    <address class="EntityDetailsPropertiesTab__address">
                        <span v-if="entity.address.street || entity.address.aptSuiteOrUnit">
                            {{ [entity.address.street, entity.address.aptSuiteOrUnit].filter(Boolean).join(', ') }}
                        </span>
                        <span
                            v-if="entity.address.city || entity.address.state || entity.address.zipCode"
                        >
                            {{ [entity.address.city, entity.address.state, entity.address.zipCode]
                                .filter(Boolean).join(', ') }}
                        </span>
                        <span v-if="entity.address.country">
                            {{ getCountryLabel(entity.address.country) }}
                        </span>
                    </address>
                </div>
            </div>

            <!-- EDIT MODE -->
            <div v-else key="edit" class="EntityDetailsPropertiesTab__form">
                <JsonSchemaCustomFormElement
                    wrap-value-mode
                    :touched="saveTouched"
                    :no-local-touch="!saveTouched"
                    :field="profileField || fields.body"
                    :depth="0"
                    :custom-component-input="getCustomComponent"
                    :disabled="!entity.active"
                    :rbac-disabled="isDisabledByRbac"
                    :strings="jsonSchemaElementStrings"
                    @change="$emit('body-change')"
                />
            </div>
        </transition>
    </EntityDetailsPropertiesTabCard>
</template>

<style lang="scss">
.EntityDetailsPropertiesTab {
    &__readSection {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 20px 0;

        // Divider between sections (not before the first one)
        & + & {
            border-top: 1px solid var(--eidQualityBlack-10);
        }
    }

    &__readSectionLabel {
        font-family: "Roboto", sans-serif;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--eidTrustGreen);
    }

    &__readMultiValue {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
}

.EntityDetailsPropertiesTabPropertiesCard {
    &__uuid {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: monospace;
    }

    &__copyIcon {
        color: var(--eidTrustGreen);
        cursor: pointer;
        flex-shrink: 0;

        &:hover {
            opacity: 0.75;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faCheck } from '@fortawesome/free-solid-svg-icons';

    import { faCopy } from '@evidentid/dashboard-commons/assets/custom-icon-definitions';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { JsonSchemaCustomFormElement, JsonSchemaFormStrings } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import JsonSchemaCountryEnumForm from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaCountryEnumForm.vue';
    import { Pointer } from '@evidentid/json-schema/FormController';
    import { JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import { TprmSchemaDisplayFormat } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import CriterionEnumInput from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionEnumInput.vue';
    import EntityDetailsAddressForm from '@/modules/entity-details/components/CustomJsonForms/EntityDetailsAddressForm/EntityDetailsAddressForm.vue';
    import EntityDetailsBooleanForm from '@/modules/entity-details/components/CustomJsonForms/EntityDetailsBooleanForm.vue';
    import EntityDetailsStringForm from '@/modules/entity-details/components/CustomJsonForms/EntityDetailsStringForm.vue';
    import { getJsonSchemaCompFunction } from '@/utils/getJsonSchemaCustomCompFuntion';

    import { getCountryLabel } from '../helpers';

    import EntityDetailsPropertiesTabCard from './EntityDetailsPropertiesTabCard.vue';

    export default defineComponent({
        name: 'EntityDetailsPropertiesTabPropertiesCard',
        components: {
            JsonSchemaCustomFormElement,
            EntityDetailsPropertiesTabCard,
            EidIcon,
        },
        props: {
            entity: {
                type: Object as PropType<Entity>,
                required: true,
            },
            isEvidentUser: {
                type: Boolean,
                default: false,
            },
            fields: {
                type: Object as PropType<Record<string, Pointer>>,
                required: true,
            },
            isEditing: {
                type: Boolean,
                required: true,
            },
            isDisabledByRbac: {
                type: Boolean,
                required: true,
            },
            saveTouched: {
                type: Boolean,
                required: true,
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
        },
        emits: [ 'body-change' ],
        data() {
            const customComponents = {
                [JsonFormType.boolean]: EntityDetailsBooleanForm,
                [JsonFormType.string]: EntityDetailsStringForm,
                [JsonFormType.enum]: CriterionEnumInput,
                [TprmSchemaDisplayFormat.address]: EntityDetailsAddressForm,
                [TprmSchemaDisplayFormat.country]: JsonSchemaCountryEnumForm,
            };
            return {
                getCustomComponent: getJsonSchemaCompFunction(customComponents),
                copyIcon: faCopy as IconDefinition,
                copyTimeout: null as any,
            };
        },
        computed: {
            jsonSchemaElementStrings(): Partial<JsonSchemaFormStrings> {
                return { customLabels: { countryLabels: this.enumLabels.countryCode }, requiredLabel: '*' };
            },
            profileField(): Pointer | null {
                return (this.fields.body as any).properties?.profile || null;
            },
            filteredDba(): string[] {
                return ((this.entity.doingBusinessAs as string[] | null) || []).filter((s) => s?.trim());
            },
            hasAddress(): boolean {
                const addr = this.entity.address;
                if (!addr) {
                    return false;
                }
                return Boolean(addr.street || addr.city || addr.state || addr.zipCode || addr.country);
            },
        },
        unmounted(): void {
            if (this.copyTimeout) {
                clearTimeout(this.copyTimeout);
            }
        },
        methods: {
            getCountryLabel(code: any): string {
                return getCountryLabel(code, this.enumLabels);
            },
            copyEntityUuid(): void {
                navigator.clipboard.writeText(this.entity.id).then(() => {
                    this.copyIcon = faCheck as IconDefinition;
                    if (this.copyTimeout) {
                        clearTimeout(this.copyTimeout);
                    }
                    this.copyTimeout = setTimeout(() => {
                        this.copyIcon = faCopy as IconDefinition;
                    }, 2000);
                }).catch(() => {
                    // Permission denied, non-secure context, etc. — leave the icon as-is
                    // rather than confirming a copy that didn't happen.
                });
            },
        },
    });
</script>
